import { Box, Card, Grid, IconButton, Typography } from '@mui/material';
import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import VisitorTypeListItem from './VisitorTypeListItem';
import IbssButton from '../../../../Components/Buttons/Button/IbssButton';
import { DateTime } from 'luxon';
import ConfirmModal from '../../../../Components/Dialogs/ConfirmDialog/ConfirmModal';
import { appContext } from '../../../../AppContext';
import IbssTextField from '../../../../Components/Inputs/TextField/IbssTextField';
import Helper from '../../../../Common/Helper';
import MediaSelector from '../../../../Components/DialogLaunchers/MediaSelector/MediaSelector';
import './VisitorTypes.scss'
import IbssChip from '../../../../Components/Navigation/Chip/IbssChip';
import AddQuestionDrawer from './AddQuestionDrawer';
import QuestionListItem from './QuestionListItem';
import Guid from '../../../../Common/Guid';
import Bin from '../../../../Components/Icons/Bin';
import IbssSvgIcon from '../../../../Components/Icons/SvgIcon/IbssSvgIcon';
import { AdvancedVisitorType, IQuestion, IStep, QuestionInputType } from './DataModels';
import { PagedResponse } from '../../../../Providers.Api/Models';
import { RouteComponentProps } from 'react-router';
import LoadingOverlay from '../../../../Components/Navigation/LoadingOverlay/LoadingOverlay';
import IbssSnackbar from '../../../../Components/Snackbar/IbssSnackbar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { QueryParams } from './QueryParams';
import IbssPageHeader from '../../../../Components/Headings/PageHeading/IbssPageHeader';

class VisitorTypes extends IbssPage<IProps, IState, QueryParams>
{

    private get labels() { return appContext().labels; }

    constructor(props: IProps)
    {
        super(props, new QueryParams());
        this.state =
        {
            buildingId: 0,
            loading: false,
            visitorTypes: [],
            showConfirmVisitorTypeDeleteModal: null,
            selectedVisitorType: {
                id: '',
                name: '',
                description: '',
                imageUrl: '',
                steps: [],
                concurrencyStamp: ''
            },
            images: [],
            visitorName: '',
            visitorDescription: '',
            unsavedChanges: false,
            showUnsavedChangesWarning: null,
            selectedStep: '',
            showAddQuestionDrawer: false,
            showDeleteQuestionConfirmation: -1, //this value is used as the index of the question in the questions array that has been attempted for deletion
            showDeleteStepConfirmation: false,
            selectedQuestion: null,
            selectedQuestionIndex: null,
            showSnackBar: ''
        };
    };

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.HubMenuManage;
        await this.getImages();
    }

    public async queryParamsDidUpdate(): Promise<void>
    {
        await this.setStateAsync({ buildingId: this.queryParams.building || 1 });
        await this.getVisitorTypes();

        if (this.state.visitorTypes.length == 0)
        {
            this.setState({
                selectedVisitorType: {
                    id: '',
                    name: '',
                    description: '',
                    imageUrl: '',
                    steps: [],
                    concurrencyStamp: ''
                },
            });
        }
    }

    private async getVisitorTypes(): Promise<void>
    {
        this.setState({ loading: true });
        try
        {
            const visitorTypes = await appContext().ibssApiClientV2.v2.byNodeid.visitorTypes.get<PagedResponse<AdvancedVisitorType[]>>({
                nodeId: this.state.buildingId,
                select: AdvancedVisitorType
            });

            await this.setStateAsync({
                visitorTypes: visitorTypes.value.map(x => VisitorTypeDetailsView.fromApiModel(x).visitorType)
            });
        }
        catch
        {
            this.setState({
                visitorTypes: [],
                selectedVisitorType: {
                    id: '',
                    name: '',
                    description: '',
                    imageUrl: '',
                    steps: [],
                    concurrencyStamp: ''
                },
            });
        }
        finally
        {
            this.setState({ loading: false });
        }
    }

    private async getImages(): Promise<void>
    {
        const fileResponseData = await appContext().webEntryApiClientV1.api.webentry.v1.file.byPath.get<IFile[]>({
            path: 'c/Visitors'
        });
        const newResponseData = fileResponseData.map((item: IFile) =>
        {
            let newItem = { ...item };
            newItem.path = `${Helper.getStorageUrl()}/images/c/Visitors/${item.name}`;
            return newItem;
        });
        this.setState({ images: newResponseData });
    }

    private async uploadImage(filename: string, file: FormData, processAsMap: boolean): Promise<void>
    {
        await appContext().webEntryApiClientV1.api.webentry.v1.file.byPath.post({
            path: 'c/Visitors/' + filename,
            processAsMapFile: false,
            body: file
        });
        this.getImages();
    }

    private addNewVisitorType(): void
    {
        if (this.state.unsavedChanges)
        {
            this.setState({ showUnsavedChangesWarning: { action: 'create', id: '' } });
        }
        else
        {
            const newStepId = Guid.new().toString();
            this.setState({
                selectedVisitorType: {
                    id: '0',
                    name: '',
                    description: '',
                    imageUrl: '',
                    steps: [
                        {
                            step: newStepId,
                            questions: []
                        }
                    ],
                    concurrencyStamp: ''
                },
                selectedStep: newStepId
            });
        }
    }

    public async save(): Promise<void>
    {
        const { selectedVisitorType } = this.state;
        if (selectedVisitorType.id == '0') //Create New
        {
            try
            {
                this.setState({ loading: true })
                const payload = VisitorTypeDetails.toApiModel(this.state.buildingId, selectedVisitorType.name, selectedVisitorType.description, selectedVisitorType.imageUrl, selectedVisitorType.steps, '').visitorType;
                const newVisitorType = await appContext().ibssApiClientV2.v2.byNodeid.visitorTypes.post<AdvancedVisitorType>({
                    nodeId: this.state.buildingId,
                    body: payload

                });
                await this.getVisitorTypes();
                this.setState({ unsavedChanges: false, loading: false, selectedVisitorType: VisitorTypeDetailsView.fromApiModel(newVisitorType).visitorType, showSnackBar: this.labels.funcVisitorTypeCreated_S });
            } catch (error)
            {
                this.setState({ loading: false });
            }

        }
        else //Update exisiting
        {
            try
            {
                this.setState({ loading: true })
                const payload = VisitorTypeDetails.toApiModel(this.state.buildingId, selectedVisitorType.name, selectedVisitorType.description, selectedVisitorType.imageUrl, selectedVisitorType.steps, this.state.selectedVisitorType.concurrencyStamp).visitorType;
                const updateVisitorType = await appContext().ibssApiClientV2.v2.byNodeid.visitorTypes.byId.put<AdvancedVisitorType>({
                    nodeId: this.state.buildingId,
                    id: this.state.selectedVisitorType.id,
                    body: payload
                });
                await this.getVisitorTypes();
                this.setState({ unsavedChanges: false, loading: false, selectedVisitorType: VisitorTypeDetailsView.fromApiModel(updateVisitorType).visitorType, showSnackBar: this.labels.funcVisitorTypeChangesSaved_S });
            } catch (error)
            {
                this.setState({ loading: false });
            }
        }
    }

    private async deleteVisitorType(): Promise<void>
    {
        if (this.state.showConfirmVisitorTypeDeleteModal)
        {
            this.setState({ loading: true });
            try
            {
                await appContext().ibssApiClientV2.v2.byNodeid.visitorTypes.byId.delete({
                    nodeId: this.state.buildingId,
                    id: this.state.showConfirmVisitorTypeDeleteModal
                });
                await this.getVisitorTypes();

                if (this.state.showConfirmVisitorTypeDeleteModal == this.state.selectedVisitorType.id)
                {
                    this.setState({
                        selectedVisitorType: {
                            id: '',
                            name: '',
                            description: '',
                            imageUrl: '',
                            steps: [],
                            concurrencyStamp: ''
                        }
                    });
                }
            } finally
            {

                this.setState({ showConfirmVisitorTypeDeleteModal: null, loading: false });
            }
        }
    }

    private editVisitorType(id: string): void
    {
        if (this.state.unsavedChanges)
        {
            this.setState({ showUnsavedChangesWarning: { action: 'edit', id: id } });
        }
        else
        {
            const selectedVisitor = this.state.visitorTypes.filter(x => id == x.id)[0]
            this.setState({
                selectedVisitorType: {
                    id: id,
                    name: selectedVisitor.name,
                    description: selectedVisitor.description,
                    imageUrl: selectedVisitor.imageUrl,
                    steps: selectedVisitor.steps,
                    concurrencyStamp: selectedVisitor.concurrencyStamp
                },
                selectedStep: selectedVisitor.steps?.length > 0 ? selectedVisitor.steps[0].step : ''
            });
        }
    }

    public visitorNameUpdated(value: string): void
    {
        this.setState({ selectedVisitorType: { ...this.state.selectedVisitorType, name: value }, unsavedChanges: true });
    }

    public visitorDescriptionUpdated(value: string): void
    {
        this.setState({ selectedVisitorType: { ...this.state.selectedVisitorType, description: value }, unsavedChanges: true });
    }

    public async unsavedChangesContinue(): Promise<void>
    {
        await this.setStateAsync({ unsavedChanges: false });
        if (this.state.showUnsavedChangesWarning?.action == 'create')
        {
            this.addNewVisitorType();
        }
        if (this.state.showUnsavedChangesWarning?.action == 'edit')
        {
            this.editVisitorType(this.state.showUnsavedChangesWarning.id);
        }
        this.setState({ showUnsavedChangesWarning: null });
    }

    public addNewStep(): void
    {
        const newStepId = Guid.new().toString();
        this.setState({
            selectedVisitorType: {
                ...this.state.selectedVisitorType,
                steps: [...this.state.selectedVisitorType.steps, { step: newStepId, questions: [] }]
            },
            selectedStep: newStepId,
            unsavedChanges: true
        });
    }

    private async deleteStep(): Promise<void>
    {

        const selectedStepArrayIndex = this.state.selectedVisitorType.steps.findIndex(x => x.step == this.state.selectedStep);
        const selectedVisitor = { ...this.state.selectedVisitorType };  // Create a shallow copy of selectedVisitorType

        const updatedSteps = this.state.selectedVisitorType.steps.map(step =>
        {
            // Make a shallow copy of each step
            const updatedStep = { ...step };
            updatedStep.questions = step.questions.map(question =>
            {
                const updatedQuestion = { ...question };
                if (updatedQuestion.InputType == QuestionInputType.TrueFalse || updatedQuestion.InputType == QuestionInputType.MultiOptionSingleSelect)
                {
                    updatedQuestion.Labels = question.Labels.map((x, index) =>
                    {
                        const updatedLabel = { ...x };
                        if (updatedLabel.goto == this.state.selectedStep)
                        {
                            updatedLabel.goto = 'next';
                        }
                        return updatedLabel;
                    });
                }
                return updatedQuestion;
            });
            return updatedStep;
        });

        // Create a new array of steps without the selected step
        const newSteps = updatedSteps.filter((x, index) => index !== selectedStepArrayIndex);

        // Create a new visitor object with the updated steps
        const updatedVisitor = { ...selectedVisitor, steps: newSteps };

        await this.setStateAsync({ selectedVisitorType: updatedVisitor, showDeleteQuestionConfirmation: -1, unsavedChanges: true, selectedStep: updatedSteps[0].step, showDeleteStepConfirmation: false });
    }

    public moveQuestionPositionUp(index: number): void
    {
        const selectedStepArrayIndex = this.state.selectedVisitorType.steps.findIndex(x => x.step === this.state.selectedStep);
        if (index === 0)
        {
            return; // Prevent moving the first question up
        }

        // Clone the selectedVisitorType and relevant parts of the state
        const selectedVisitor = {
            ...this.state.selectedVisitorType,
            steps: this.state.selectedVisitorType.steps.map((step, stepIndex) =>
            {
                if (stepIndex === selectedStepArrayIndex)
                {
                    // Create a new array with the swapped questions using spread operator
                    const questions = [
                        ...step.questions.slice(0, index - 1),  // All questions before the index - 1
                        step.questions[index],                   // The question at the current index
                        step.questions[index - 1],               // The question at index - 1
                        ...step.questions.slice(index + 1)       // All questions after the index
                    ];
                    return {
                        ...step,
                        questions
                    };
                }
                return step;
            })
        };

        this.setState({ selectedVisitorType: selectedVisitor, unsavedChanges: true });
    }


    public moveQuestionPositionDown(index: number): void
    {
        const selectedStepArrayIndex = this.state.selectedVisitorType.steps.findIndex(x => x.step === this.state.selectedStep);
        if (index === this.state.selectedVisitorType.steps[selectedStepArrayIndex].questions.length - 1)
        {
            return; // Prevent moving the last question down
        }

        // Clone the selectedVisitorType and relevant parts of the state
        const selectedVisitor = {
            ...this.state.selectedVisitorType,
            steps: this.state.selectedVisitorType.steps.map((step, stepIndex) =>
            {
                if (stepIndex === selectedStepArrayIndex)
                {
                    // Clone the questions array and move the question at the given index
                    const updatedQuestions = [
                        ...step.questions.slice(0, index),        // All questions before the index
                        step.questions[index + 1],               // The question at index + 1
                        step.questions[index],                   // The question at the current index
                        ...step.questions.slice(index + 2)       // All questions after the index + 1
                    ];
                    return {
                        ...step,
                        questions: updatedQuestions
                    };
                }
                return step;
            })
        };

        this.setState({ selectedVisitorType: selectedVisitor, unsavedChanges: true });
    }

    private addQuestion(questionDetails: IQuestion): void
    {
        const selectedStepArrayIndex = this.state.selectedVisitorType.steps.findIndex(x => x.step === this.state.selectedStep);

        // Clone selectedVisitorType and relevant parts of the state
        const selectedVisitorType = {
            ...this.state.selectedVisitorType,
            steps: this.state.selectedVisitorType.steps.map((step, stepIndex) =>
            {
                if (stepIndex === selectedStepArrayIndex)
                {
                    // Create a new questions array with the new question added immutably
                    const updatedQuestions = [
                        ...step.questions,      // Existing questions
                        questionDetails         // New question to add
                    ];
                    return {
                        ...step,
                        questions: updatedQuestions
                    };
                }
                return step;
            })
        };

        this.setState({
            selectedVisitorType: selectedVisitorType,
            showAddQuestionDrawer: false,
            unsavedChanges: true
        });
    }

    private updateQuestion(questionDetails: IQuestion): void
    {
        if (this.state.selectedQuestionIndex != null)
        {
            // Clone selectedVisitorType to maintain immutability
            const selectedVisitorType = {
                ...this.state.selectedVisitorType,
                steps: this.state.selectedVisitorType.steps.map((step, stepIndex) =>
                {
                    if (step.step === this.state.selectedStep)
                    {
                        // Clone the questions array and update the specific question
                        const updatedQuestions = step.questions.map((question, index) =>
                            index === this.state.selectedQuestionIndex ? questionDetails : question
                        );
                        return {
                            ...step,
                            questions: updatedQuestions
                        };
                    }
                    return step;
                })
            };

            this.setState({
                selectedVisitorType,
                showAddQuestionDrawer: false,
                unsavedChanges: true
            });
        }
    }

    private deleteQuestion(): void
    {
        const selectedStepArrayIndex = this.state.selectedVisitorType.steps.findIndex(x => x.step === this.state.selectedStep);

        // Clone selectedVisitorType and relevant parts of the state
        const selectedVisitorType = {
            ...this.state.selectedVisitorType,
            steps: this.state.selectedVisitorType.steps.map((step, stepIndex) =>
            {
                if (stepIndex === selectedStepArrayIndex)
                {
                    // Create a new questions array with the question removed immutably
                    const updatedQuestions = [
                        ...step.questions.slice(0, this.state.showDeleteQuestionConfirmation),  // All questions before the one to delete
                        ...step.questions.slice(this.state.showDeleteQuestionConfirmation + 1) // All questions after the one to delete
                    ];
                    return {
                        ...step,
                        questions: updatedQuestions
                    };
                }
                return step;
            })
        };

        this.setState({
            selectedVisitorType: selectedVisitorType,
            showDeleteQuestionConfirmation: -1,
            unsavedChanges: true
        });
    }

    private getQuestionNameField(question: IQuestion): string
    {
        if (question.InputType == QuestionInputType.MultiOptionSingleSelect || question.InputType == QuestionInputType.TrueFalse || question.InputType == QuestionInputType.Open)
        {
            return question.QuestionName
        }
        else if (question.InputType == QuestionInputType.Policy)
        {
            return question.Name
        }
        else if ((question.InputType == QuestionInputType.Header || question.InputType == QuestionInputType.SubHeader))
        {
            return question.Value
        }
        else
        {
            return ''
        }
    }

    private visitorTypeNameAlreadyExists(): boolean
    {
        return this.state.visitorTypes.some(x => x.name == this.state.selectedVisitorType.name && x.id != this.state.selectedVisitorType.id);
    }

    public render(): JSX.Element
    {
        const questionTypes = [
            { label: this.labels.funcQuestionTypeTrueFalse_S, value: QuestionInputType.TrueFalse },
            { label: this.labels.funcQuestionTypeOpen_S, value: QuestionInputType.Open },
            { label: this.labels.funcQuestionTypePolicy_S, value: QuestionInputType.Policy },
            { label: this.labels.funcQuestionTypeMultiSelect_S, value: QuestionInputType.MultiOptionSingleSelect },
            { label: this.labels.funcQuestionTypeHeading_S, value: QuestionInputType.Header },
            { label: this.labels.funcQuestionTypeSubheading_S, value: QuestionInputType.SubHeader },
            { label: this.labels.funcQuestionTypeImage_S, value: QuestionInputType.Image },
        ]

        const visitorTypeNameError = this.visitorTypeNameAlreadyExists() || this.state.selectedVisitorType.name.length == 0;
        return (
            <div className='m-3'>
                {
                    this.state.loading &&
                    <LoadingOverlay />
                }
                <Grid container spacing={2}>
                    <Grid item xs={4}>
                        <Card style={{ padding: '26px' }}>
                            <Box>
                            <IbssPageHeader
                                title={this.labels.funcVisitorTypes_Short}
                                description={this.labels.funcVisitorTypesSubtitle_Message}
                            />
                                {
                                    this.state.visitorTypes.map(visitorType => (
                                        <VisitorTypeListItem
                                            selected={this.state.selectedVisitorType.id == visitorType.id}
                                            id={visitorType.id}
                                            name={visitorType.name}
                                            onDelete={(id) => this.setState({ showConfirmVisitorTypeDeleteModal: id })}
                                            onEdit={(id) => this.editVisitorType(id)}
                                        />
                                    ))
                                }
                                <Box textAlign={'center'} marginBottom={4} marginTop={4}>
                                    <IbssButton fullWidth variant='contained' color='primary' onClick={() => this.addNewVisitorType()}>{this.labels.funcCreateNewVisitorType_Short}</IbssButton>
                                </Box>
                            </Box>
                            {/* Delete visitor type confirmation modal */}
                            <ConfirmModal
                                show={this.state.showConfirmVisitorTypeDeleteModal != null}
                                modalHeading={this.labels.HubLabelModalConfirmTitle}
                                modalMessage={this.labels.funcVisitorDeleteConfirmation_Message}
                                handleModal={() => this.setState({ showConfirmVisitorTypeDeleteModal: null })}
                                okButton={() => this.deleteVisitorType()}
                                cancelButtonText={this.labels.HubButtonCancel}
                                confirmButtonColor={'error'}
                                confirmButtonText={this.labels.HubButtonDelete}
                            />
                            {/* Unsaved changes modal */}
                            <ConfirmModal
                                show={this.state.showUnsavedChangesWarning != null}
                                modalHeading={this.labels.funcUnsavedChanges_S}
                                modalMessage={this.labels.funcUnsavedChangesVisitorTypeMessage_S}
                                handleModal={() => this.setState({ showUnsavedChangesWarning: null })}
                                okButton={() => this.unsavedChangesContinue()}
                                cancelButtonText={this.labels.HubButtonCancel}
                                confirmButtonText={this.labels.HubButtonContinue}
                            />
                            {/* Delete question confirmation modal */}
                            <ConfirmModal
                                show={this.state.showDeleteQuestionConfirmation != -1}
                                modalHeading={this.labels.HubLabelModalConfirmTitle}
                                modalMessage={this.labels.funcDeleteQuestionConfirmMessage_S}
                                handleModal={() => this.setState({ showDeleteQuestionConfirmation: -1 })}
                                okButton={() => this.deleteQuestion()}
                                cancelButtonText={this.labels.HubButtonCancel}
                                confirmButtonColor={'error'}
                                confirmButtonText={this.labels.HubButtonDelete}
                            />
                            {/* Delete step confirmation modal */}
                            <ConfirmModal
                                show={this.state.showDeleteStepConfirmation}
                                modalHeading={this.labels.HubLabelModalConfirmTitle}
                                modalMessage={this.labels.funcDeleteStepConfirm_Message}
                                handleModal={() => this.setState({ showDeleteStepConfirmation: false })}
                                okButton={() => this.deleteStep()}
                                cancelButtonText={this.labels.HubButtonCancel}
                                confirmButtonColor={'error'}
                                confirmButtonText={this.labels.HubButtonDelete}
                            />
                            <AddQuestionDrawer
                                open={this.state.showAddQuestionDrawer}
                                close={() => this.setState({ showAddQuestionDrawer: false })}
                                onCancel={() => this.setState({ showAddQuestionDrawer: false })}
                                onSaveQuestion={(questionDetails) => this.state.selectedQuestion == null || this.state.selectedQuestionIndex == null ? this.addQuestion(questionDetails) : this.updateQuestion(questionDetails)}
                                defaultValues={this.state.selectedQuestion}
                                images={this.state.images}
                                uploadFile={(filename, file, processAsMap) => this.uploadImage(filename, file, processAsMap)}
                                steps={this.state.selectedVisitorType.steps}
                            />
                        </Card>
                    </Grid>
                    <Grid item xs={8}>
                        {
                            this.state.selectedVisitorType.id != '' &&
                            <Card>
                                <Box margin={4} display={'flex'}>
                                    <Box width={'80%'}>
                                    <IbssPageHeader
                                        title={this.state.selectedVisitorType.id === '0' ? this.labels.Admin_VisitorTypes_CreateNewTitle : this.labels.funcManageType_Short}
                                        description={this.state.selectedVisitorType.id === '0' ? this.labels.Admin_VisitorTypes_CreateNewSubtitle  : this.labels.funcSelectVisitorInfo_Message }
                                    />
                                    </Box>
                                    <Box width={'20%'}>
                                        <Box display={'flex'} justifyContent={'flex-end'}>
                                        <IbssButton variant='contained' color='primary' disabled={visitorTypeNameError} onClick={() => this.save()}>{this.labels.funcSaveAndUpdate_S}</IbssButton>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box margin={4} display={'flex'} justifyContent={'space-between'}>
                                    <Box width={'48%'}>
                                        <div className='mb-2'>
                                            <IbssTextField error={visitorTypeNameError} helperText={this.visitorTypeNameAlreadyExists() ? this.labels.funcVisitorTypeNameExists_Message : ''} value={this.state.selectedVisitorType.name} required onChange={(e) => this.visitorNameUpdated(e.target.value)} fullWidth variant='standard' label={this.labels.funcTypeName_S} />
                                        </div>
                                        <div>
                                            <IbssTextField value={this.state.selectedVisitorType.description} onChange={(e) => this.visitorDescriptionUpdated(e.target.value)} fullWidth variant='standard' label={this.labels.funcTypeDescription_S} maxLength={500} />
                                        </div>
                                    </Box>
                                    <Box width={'48%'} paddingTop={2}>
                                        <MediaSelector
                                            options={this.state.images.map(i => ({ name: i.name, url: i.path }))}
                                            imageSelected={file => this.setState({ selectedVisitorType: { ...this.state.selectedVisitorType, imageUrl: file.url }, unsavedChanges: true })}
                                            uploadFile={(filename, file, processAsMap) => this.uploadImage(filename, file, processAsMap)}
                                            defaultSelectedImage={this.state.selectedVisitorType.imageUrl}
                                        />
                                    </Box>
                                </Box>
                                <hr />
                                <Box margin={4}>
                                    {
                                        this.state.selectedVisitorType.steps.map((x, index) => (
                                            this.state.selectedStep == x.step ?
                                                <IbssChip
                                                    onClick={() => this.setState({ selectedStep: x.step })}
                                                    sx={{ backgroundColor: 'var(--ui-secondary)', color: "var(--ui-light-text) !important", cursor: 'pointer', marginRight: '8px', marginBottom: '8px' }}
                                                    label={this.labels.funcStep_S + ' ' + (index + 1)}
                                                />
                                                :
                                                <IbssChip
                                                    onClick={() => this.setState({ selectedStep: x.step })}
                                                    label={this.labels.funcStep_S + ' ' + (index + 1)}
                                                    sx={{ cursor: 'pointer', marginRight: '8px', marginBottom: '8px' }}
                                                />
                                        ))
                                    }
                                    <IbssChip
                                        onClick={() => this.addNewStep()}
                                        sx={{ backgroundColor: 'var(--ui-background-alternate)', color: "var(--ui-text) !important", border: '1px solid var(--ui-text)', cursor: 'pointer', marginBottom: '8px' }}
                                        label={this.labels.HubButtonAdd + ' +'}
                                    />
                                    <div style={{ justifySelf: 'right' }}>
                                        <IbssButton
                                            disabled={this.state.selectedVisitorType.steps.length == 1}
                                            variant='outlined'
                                            color='error'
                                            className='mr-2'
                                            onClick={() => this.setState({ showDeleteStepConfirmation: true })}>
                                            <IbssSvgIcon className='mr-1' sx={{ paddingTop: '5px' }} color={this.state.selectedVisitorType.steps.length == 1 ? 'disabled' : 'error'}>
                                                <Bin />
                                            </IbssSvgIcon>
                                            {this.labels.funcDeleteStep_S}
                                        </IbssButton>
                                    </div>
                                    {
                                        this.state.selectedVisitorType.steps.filter(x => x.step == this.state.selectedStep)[0]?.questions.length > 0 ?
                                            <div className='mt-4'>
                                                <Grid container sx={{ marginBottom: '7px' }}>
                                                    <Grid item xs={6}>
                                                        {this.labels.funcQuestion_S}
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        {this.labels.funcInputType_S}
                                                    </Grid>
                                                </Grid>
                                                {
                                                    this.state.selectedVisitorType.steps.filter(x => x.step == this.state.selectedStep)[0]?.questions.map((question, index) => (
                                                        <div className='mb-1'>
                                                            <QuestionListItem
                                                                onArrowUp={() => this.moveQuestionPositionUp(index)}
                                                                onArrowDown={() => this.moveQuestionPositionDown(index)}
                                                                name={this.getQuestionNameField(question)}
                                                                type={questionTypes.filter(x => x.value == question.InputType)[0]?.label}
                                                                onEdit={() => this.setState({ selectedQuestion: question, selectedQuestionIndex: index, showAddQuestionDrawer: true })}
                                                                onDelete={() => this.setState({ showDeleteQuestionConfirmation: index })}
                                                            />
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                            :
                                            <div className='noContentBox mt-4'>
                                                <Typography variant='h6' className='mb-3'>{this.labels.funcNoContentAdded_Message}</Typography>
                                                <Typography variant='subtitle1' className='mb-3'>{this.labels.funcAddQuestion_Message}</Typography>
                                            </div>
                                    }
                                </Box>
                                <Box margin={4}>
                                    <IbssButton variant='contained' color='secondary' fullWidth onClick={() => this.setState({ showAddQuestionDrawer: true, selectedQuestion: null, selectedQuestionIndex: null })}>{this.labels.funcAddNewQuestion_S}</IbssButton>
                                </Box>
                            </Card>
                        }
                    </Grid>
                </Grid>
                <IbssSnackbar
                    open={this.state.showSnackBar != ''}
                    autoHideDuration={4000}
                    onClose={() => this.setState({ showSnackBar: '' })}
                    message={this.state.showSnackBar}
                    action={<CheckCircleIcon color='success' />}
                />
            </div>
        )
    }
}

export default VisitorTypes;

export interface IProps extends RouteComponentProps
{
}

export interface IState
{
    buildingId: number;
    loading: boolean;
    visitorTypes: IVisitorTypeDetailsView[];
    showConfirmVisitorTypeDeleteModal: string | null;
    selectedVisitorType: IVisitorTypeDetailsView;
    images: IFile[];
    visitorName: string;
    visitorDescription: string;
    unsavedChanges: boolean;
    showUnsavedChangesWarning: null | { action: 'create' | 'edit', id: string };
    selectedStep: string;
    showAddQuestionDrawer: boolean;
    showDeleteQuestionConfirmation: number;
    showDeleteStepConfirmation: boolean;
    selectedQuestion: IQuestion | null;
    selectedQuestionIndex: number | null;
    showSnackBar: string;
}

export interface IFile
{
    name: string;
    path: string;
    lastModified: string;
    createdAt: string;
    size: string;
}

export interface IVisitorTypeDetailsView
{
    id: string;
    name: string;
    description: string;
    imageUrl: string;
    steps: IStepView[];
    concurrencyStamp: string;
}

export class VisitorTypeDetailsView
{
    public visitorType = {
        id: '',
        name: '',
        description: '',
        imageUrl: '',
        steps: [] as IStepView[],
        concurrencyStamp: ''
    };

    public static fromApiModel(visitorType: AdvancedVisitorType): VisitorTypeDetailsView
    {
        return {
            visitorType: {
                id: visitorType.VisitorTypeId,
                name: visitorType.Name,
                description: visitorType.Description,
                imageUrl: visitorType.Icon,
                steps: visitorType.VisitorTypeWorkFlow.Steps ? visitorType.VisitorTypeWorkFlow.Steps.map(x => ({ step: x.Step, questions: x.Questions })) : [],
                concurrencyStamp: visitorType.ConcurrencyStamp
            }
        }
    };
}

export class VisitorTypeDetails
{
    public visitorType = {
        Node_Id: 0,
        Name: '',
        Description: '',
        Icon: '',
        VisitorTypeWorkFlow: {
            Steps: [] as IStep[]
        },
        ConcurrencyStamp: ''
    };

    public static toApiModel(nodeId: number, name: string, description: string, icon: string, steps: IStepView[], concurrencyStamp: string): VisitorTypeDetails
    {
        return {
            visitorType: {
                Node_Id: nodeId,
                Name: name,
                Description: description,
                Icon: icon,
                VisitorTypeWorkFlow: {
                    Steps:
                        steps.map(x => ({
                            Step: x.step,
                            Description: "",
                            Questions: x.questions.map(question => ({
                                Id: question.Id,
                                InputType: question.InputType,
                                Name: question.Name,
                                HelperText: question.HelperText,
                                Value: question.Value,
                                goto: question.goto,
                                Required: question.Required,
                                MaxLength: question.MaxLength,
                                PlaceHolderText: question.PlaceHolderText,
                                InputHeight: question.InputHeight,
                                PolicyText: question.PolicyText,
                                RequiredConfirmation: question.RequiredConfirmation,
                                ConfirmationText: question.ConfirmationText,
                                QuestionName: question.QuestionName,
                                Labels: question.Labels,
                                Answers: []
                            }))
                        }))
                },
                ConcurrencyStamp: concurrencyStamp
            }
        }
    };
}

export interface IStepView
{
    step: string;
    questions: IQuestion[]
}